import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useConfirm, useDestructiveAction } from '@/components/feedback/dialog';

import {
  customersResource,
  getCustomer,
  getCustomerDevice,
  getCustomerForEdit,
  lookupZip,
  setCustomerStatus,
} from './api';

export const customerKeys = {
  all: ['customers'] as const,
  detail: (id: string) => [...customerKeys.all, 'detail', id] as const,
  editDetail: (id: string) => [...customerKeys.all, 'edit', id] as const,
  /** Nested under the Customer so a record change evicts its devices with it — the shape
   *  `customerMapKeys.device` already uses. */
  device: (id: string, unitId: string) => [...customerKeys.all, id, 'device', unitId] as const,
};

/** Customer Detail header (102103). Disabled when `id` is empty. */
export function useCustomerDetail(id: string) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => getCustomer(id),
    enabled: Boolean(id),
  });
}

/** One Device unit's details page (feature 119076 ticket 03). Disabled until both ids are in the
 *  path: the Customer half is not decoration, it is what scopes the request. */
export function useCustomerDevice(id: string, unitId: string) {
  return useQuery({
    queryKey: customerKeys.device(id, unitId),
    queryFn: () => getCustomerDevice(id, unitId),
    enabled: Boolean(id) && Boolean(unitId),
  });
}

/** Edit-form prefill (ticket 03). Distinct key from `useCustomerDetail` so the display-only
 *  102098 detail and the full-address edit shape never overwrite each other in the cache. */
export function useCustomerEditDetail(id: string) {
  return useQuery({
    queryKey: customerKeys.editDetail(id),
    queryFn: () => getCustomerForEdit(id),
    enabled: Boolean(id),
  });
}

/** Customer write hooks. Create/update/delete come from the shared `createResource` factory;
 *  each invalidates `['customers']` so the listing refetches. */
export const useCreateCustomer = customersResource.useCreate;
export const useUpdateCustomer = customersResource.useUpdate;
export const useDeleteCustomer = customersResource.useDelete;

/** Address ZIP lookup (ticket 113129) — resolves State + City + city options for a 5-digit ZIP.
 *  Disabled until the ZIP is exactly 5 digits; the result is long-lived (zip→city/state is stable)
 *  and never retried (an unknown ZIP is a 422, not a transient error). */
export function useZipLookup(zipcode: string) {
  return useQuery({
    queryKey: [...customerKeys.all, 'zip-lookup', zipcode] as const,
    queryFn: () => lookupZip(zipcode),
    enabled: /^\d{5}$/.test(zipcode),
    staleTime: 60 * 60_000,
    retry: false,
  });
}

/** PATCH /customers/admin/:id/status. Hand-written (not `createResource.useSetStatus`) because the
 *  contract uses the `/status` sub-path; invalidates `['customers']`, which prefix-matches both the
 *  listing key and `customerKeys.detail(id)` — so the Customer Detail header's own badge flips too
 *  (AC 102103's "Status/badge update"), with no refetch at the call site. */
export function useSetCustomerStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      setCustomerStatus(id, status),
    onSuccess: () => void qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

/**
 * The Enable/Disable confirmation + mutation + feedback, as one call.
 *
 * AC 102103 says the header's overflow menu applies "the same rules as defined in the
 * Enable/Disable Customer user story" — so this is deliberately shared rather than copied:
 * the Customer listing's row menu and the Customer Detail header are two surfaces of one
 * behaviour, and a second literal copy of these five strings is a drift hazard (ADO 119031).
 */
export function useCustomerStatusChange() {
  const setStatus = useSetCustomerStatus();
  const { confirm } = useConfirm();
  const { run } = useDestructiveAction();

  return (id: string, status: 'active' | 'inactive') => {
    const enabling = status === 'active';
    return confirm({
      title: enabling ? 'Enable Customer' : 'Disable Customer',
      subtitle: enabling
        ? "Are you sure you want to enable this customer's account? They will regain access to the webstore and FieldPro Tracking App."
        : "Are you sure you want to disable this customer's account? They will no longer be able to log in to the Webstore or FieldPro Tracking App.",
      confirmText: enabling ? 'Enable' : 'Disable',
      onConfirm: async () => {
        await run(() => setStatus.mutateAsync({ id, status }), {
          success: enabling ? 'Customer enabled successfully' : 'Customer disabled successfully',
          errorFallback: 'Failed to update customer status. Please try again.',
        });
      },
    });
  };
}
